<template>
    <div class="row p-1 mt-3">
      <div class="col">
        <a-table style="width: 100%;" 
          :columns="projectDataInfo.columns"
          :data-source="projectDataInfo.data"
          :expandedRowKeys="projectDataInfo.expandedRows"
          :pagination="projectDataInfo.pagination"
          :loading="projectDataInfo.isLoading"
          @expand="expandOnClick" @change="reloadProjectData">
          <template #title>
            <a-space size="middle">
              <new-project-modal @submit-success="createProjectSuccess"/>
              <a-button type="primary" @click="downloadImportUserSample">
                <DownloadOutlined />&nbsp;下載人員匯入範例
              </a-button>
              <a-dropdown style="float: right;">
                <template #overlay>
                  <a-menu @click="updateQueryDuration">
                    <a-menu-item key="LastThreeMonth">
                      <CalendarTwoTone />&nbsp;三個月內 - 新增之專案
                    </a-menu-item>
                    <a-menu-item key="LastSixMonth">
                      <CalendarTwoTone />&nbsp;六個月內 - 新增之專案
                    </a-menu-item>
                    <a-menu-item key="LastYear">
                      <CalendarTwoTone />&nbsp;一年內 - 新增之專案
                    </a-menu-item>
                    <a-menu-item key="LastTwoYear">
                      <CalendarTwoTone />&nbsp;兩年內 - 新增之專案
                    </a-menu-item>
                    <a-menu-item key="AllYears">
                      <CalendarTwoTone />&nbsp;所有已新增之專案
                    </a-menu-item>
                  </a-menu>
                </template>
                <a-button>
                  <CalendarTwoTone />{{ queryDurationNames.get(projectDataInfo.queryDuration) }}&nbsp;<DownOutlined />
                </a-button>
              </a-dropdown>
            </a-space>
            <a-input-search v-model:value="projectDataInfo.searchText" @change="onSearchChange" 
                placeholder="輸入搜尋文字" style="width: 240px; float: right;" @search="onSearch"/>
          </template>
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'projectNo'">
              <div class="hyperlink" @click="expandOnClickProject(record)">
                {{ record.projectNo }}
              </div>
            </template>
            <template v-else-if="column.key === 'questTypeActions'">
              <template v-for="questTypeAction in record.questTypeActions" :key="questTypeAction">
                <span style="padding-top: 5px; padding-bottom: 5px;">
                  <a-tag :color="questTypeColors.get(questTypeAction.questType)">
                    {{ questTypeNames.get(questTypeAction.questType) }}
                  </a-tag>
                </span>
              </template>
            </template>
            <template v-else-if="column.key === 'projectStatus'">
              <span>
                <a-tag :color="projectStatusColors.get(record.status)">
                  <template #icon v-if="record.status === 'COMPLETED'">
                    <CheckCircleOutlined />
                  </template>
                  <template #icon v-else-if="record.status === 'PROCESSING'">
                    <SyncOutlined />
                  </template>
                  <template #icon v-else-if="record.status === 'WAIT_PROCESS'">
                    <ClockCircleOutlined />
                  </template>
                  <template #icon v-else-if="record.status === 'VOID'">
                    <MinusCircleOutlined />
                  </template>
                  {{ projectStatusNames.get(record.status) }}
                </a-tag>
              </span>
            </template>
            <template v-else-if="column.key === 'expireDateRange'">
              <CalendarOutlined />&nbsp;&nbsp;{{ record.mingoExpireDateStart }}<br />
              <SwapRightOutlined /><br />
              <CalendarOutlined />&nbsp;&nbsp;{{ record.mingoExpireDateEnd }}
            </template>
            <template v-else-if="column.key === 'checkDateRange'">
              <CalendarOutlined />&nbsp;&nbsp;{{ record.mingoCheckDateStart }}<br />
              <SwapRightOutlined /><br />
              <CalendarOutlined />&nbsp;&nbsp;{{ record.mingoCheckDateEnd }}
            </template>
            <template v-else-if="column.key === 'action'">
              <a-dropdown :trigger="['click']">
                <template #overlay>
                  <a-menu>
                    <a-menu-item key="1" @click="downloadUnImportUser(record)">
                      <DownloadOutlined />&nbsp;下載未匯入人員&nbsp;
                    </a-menu-item>
                    <a-menu-item key="9" @click="downloadImportedUser(record)">
                      <DownloadOutlined />&nbsp;下載已匯入名單&nbsp;
                    </a-menu-item>
                    <a-menu-item key="2">
                      <a-upload :multiple="false" :showUploadList="false"
                        :beforeUpload="beforeUploadProjectUser" @change="uploadProjectUser(record)">
                        <UploadOutlined />&nbsp;匯入人員名單&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </a-upload>
                    </a-menu-item>
                    <review-project-quest-modal :project="record" />
                    <edit-project-modal :project="record" @submit-success="editProjectSuccess" />
                    <project-i18n-modal :project="record" @submit-success="saveProjectI18nSuccess" />
                    <a-menu-item key="5" @click="deleteProject(record)">
                      <DeleteOutlined />&nbsp;刪除專案&nbsp;
                    </a-menu-item>
                    <review-project-user-modal :project="record" />
                    <a-menu-item key="7" @click="copyQuestUrl(record.questUrl)">
                      <CopyOutlined />&nbsp;複製問卷網址&nbsp;
                    </a-menu-item>
                    <a-menu-item key="8" @click="downloadQuestQrCode(record)">
                      <QrcodeOutlined />&nbsp;下載&nbsp;QRCode&nbsp;
                    </a-menu-item>
                  </a-menu>
                </template>
                <a-button> 更多 <DownOutlined /> </a-button>
              </a-dropdown>
            </template>
          </template>
          <template #expandedRowRender="{ record, index }">
            <a-table
              :pagination="false"
              :columns="projectDataInfo.innerColumns"
              :data-source="record.questTypeActions">
              <template #title>
                <a-space :size="25">
                  <a-checkbox v-model:checked="record.includeUnImport">匯出時包含未匯入人員</a-checkbox>
                  <a-checkbox v-model:checked="projectDataInfo.categorizeByUnitName">匯出時依單位名稱分類</a-checkbox>
                  <a-checkbox v-model:checked="projectDataInfo.exportByProjectName">以專案名稱為匯出檔名</a-checkbox>
                </a-space>
              </template>
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'questType'">
                  <span>
                    <a-tag :color="questTypeColors.get(record.questType)">
                      {{ questTypeNames.get(record.questType) }}
                    </a-tag>
                  </span>
                </template>
                <template v-else-if="column.key === 'questActions'">
                  <span v-for="questAction in record.questActions" :key="questAction">
                    <template v-if="questAction === 'ExportXlsx'">
                      <a-button type="link" @click="exportXlsxProjectQuest(record, index)">{{ actionNames.get(questAction) }}</a-button>
                      <a-divider type="vertical" />
                    </template>
                    <template v-if="questAction === 'ExportXls'">
                      <a-button type="link" @click="exportXlsProjectQuest(record, index)">{{ actionNames.get(questAction) }}</a-button>
                    </template>
                    <template v-else-if="questAction === 'ExportPdf'">
                      <a-divider type="vertical" />
                      <a-dropdown :trigger="['click']">
                        <template #overlay>
                          <a-menu>
                            <a-menu-item key="Selection" disabled>
                              選擇語系
                            </a-menu-item>
                            <a-menu-divider />
                            <template v-if=" record.questType === 'GENERAL_PHYSICAL'">
                              <a-menu-item key="Chinese" @click="exportPdfProjectQuest(record, index, 'Chinese')">
                                <DownloadOutlined />&nbsp;繁體中文&nbsp;
                              </a-menu-item>
                              <a-menu-item key="Thai" @click="exportPdfProjectQuest(record, index, 'Thai')">
                                <DownloadOutlined />&nbsp;泰文&nbsp;
                              </a-menu-item>
                              <a-menu-item key="English" @click="exportPdfProjectQuest(record, index, 'English')">
                                <DownloadOutlined />&nbsp;英文&nbsp;
                              </a-menu-item>
                              <a-menu-item key="Vietnamese" @click="exportPdfProjectQuest(record, index, 'Vietnamese')">
                                <DownloadOutlined />&nbsp;越南文&nbsp;
                              </a-menu-item>
                              <a-menu-item key="Indonesian" @click="exportPdfProjectQuest(record, index, 'Indonesian')">
                                <DownloadOutlined />&nbsp;印尼文&nbsp;
                              </a-menu-item>
                            </template>
                            <template v-else>
                              <a-menu-item key="Chinese" @click="exportPdfProjectQuest(record, index, 'Chinese')">
                                <DownloadOutlined />&nbsp;繁體中文&nbsp;
                              </a-menu-item>
                              <a-menu-item key="English" @click="exportPdfProjectQuest(record, index, 'English')">
                                <DownloadOutlined />&nbsp;英文&nbsp;
                              </a-menu-item>
                            </template>
                          </a-menu>
                        </template>
                        <a-button type="link">{{ actionNames.get(questAction) }}&nbsp;<DownOutlined /></a-button>
                      </a-dropdown>
                    </template>
                  </span>
                </template>
              </template>
            </a-table>
          </template>
        </a-table>
      </div>
    </div>
</template>
<script>
import {
  DownloadOutlined, CheckCircleOutlined, UploadOutlined, SyncOutlined,
  DownOutlined, ClockCircleOutlined, CalendarOutlined, MinusCircleOutlined,
  DeleteOutlined, CopyOutlined, QrcodeOutlined, SwapRightOutlined, CalendarTwoTone
} from "@ant-design/icons-vue";
import { message } from 'ant-design-vue';

import { ProjectStatusNames, ProjectStatusColors, QuestTypeNames, 
  QuestTypeColors, ExportActionNames, LangOptionNames
} from "@/common";
import common from "@/common";
import config from "@/config";
import NewProjectModal from './NewProjectModal.vue';
import EditProjectModal from './EditProjectModal.vue';
import ReviewProjectUserModal from "./ReviewProjectUserModal.vue";
import ReviewProjectQuestModal from "./ReviewProjectQuestModal.vue";
import ProjectI18nModal from "./ProjectI18nModal.vue";

export default {
  name: "ProjectListTable",
  components: {
    DownloadOutlined, CheckCircleOutlined, UploadOutlined, SyncOutlined, 
    DownOutlined, ClockCircleOutlined, CalendarOutlined, MinusCircleOutlined,
    DeleteOutlined, CopyOutlined, QrcodeOutlined, CalendarTwoTone,
    SwapRightOutlined, NewProjectModal, EditProjectModal, ReviewProjectUserModal, 
    ReviewProjectQuestModal, ProjectI18nModal
  },
  mounted() {
    this.projectDataInfo.queryDuration = common.getQueryDuration("LastThreeMonth");
    this.onSearch();
  },
  created() {
    if (this.$route.query.otp) {
      this.$router.push({ path: '/home' });
    }
  },
  data() {
    const queryDurationNames = new Map();
    queryDurationNames.set("LastThreeMonth", "三個月內 - 新增之專案");
    queryDurationNames.set("LastSixMonth", "六個月內 - 新增之專案");
    queryDurationNames.set("LastYear", "一年內 - 新增之專案");
    queryDurationNames.set("LastTwoYear", "兩年內 - 新增之專案");
    queryDurationNames.set("AllYears", "所有已新增之專案");

    return {
      queryDurationNames: queryDurationNames,
      projectStatusNames: ProjectStatusNames,
      projectStatusColors: ProjectStatusColors,
      questTypeNames: QuestTypeNames,
      questTypeColors: QuestTypeColors,
      actionNames: ExportActionNames,
      langOptionNames: LangOptionNames,
      projectDataTable: {},
      projectDataInfo: {
        queryDuration: "LastThreeMonth",
        categorizeByUnitName: true,
        exportByProjectName: false,
        uploadFile: null,
        isLoading: false,
        searchText: "",
        pagination: {
          current: 1
        },
        statusFilter: [],
        questTypeFilter: [],
        expandedRows: [],
        columns: [
          {
            title: "專案編號",
            dataIndex: "projectNo",
            key: "projectNo",
            align: "left",
            width: "100px",
          },
          {
            title: "專案名稱",
            dataIndex: "projectName",
            key: "projectName",
            align: "left",
          },
          {
            title: "客戶名稱",
            dataIndex: "companyName",
            key: "companyName",
            align: "left",
          },
          {
            title: "專案狀態",
            dataIndex: "projectStatus",
            key: "projectStatus",
            align: "center",
            width: "140px",
            filters: [
              {
                text: "待進行",
                value: "WAIT_PROCESS",
              },
              {
                text: "進行中",
                value: "PROCESSING",
              },
              {
                text: "已完成",
                value: "COMPLETED",
              },
            ]
          },
          {
            title: "體檢日期",
            key: "checkDateRange",
            align: "center",
            width: "130px",
          },
          {
            title: "問卷填寫日期",
            key: "expireDateRange",
            align: "center",
            width: "130px",
          },
          {
            title: "問卷類型",
            dataIndex: "questTypeActions",
            key: "questTypeActions",
            align: "left",
            width: "250px",
            filters: common.getAuthorizedQuestTypeFilter()
          },
          {
            title: "動作",
            key: "action",
            align: "center",
            width: "90px",
          },
        ],
        innerColumns: [
          {
            title: "問卷類型",
            dataIndex: "questType",
            key: "questType",
            width: "150px",
          },
          {
            title: "動作",
            dataIndex: "questActions",
            key: "questActions",
          },
        ],
        data: [],
      },
    };
  },
  methods: {
    updateQueryDuration(menuItem) {
      if (this.projectDataInfo.queryDuration === menuItem.key) {
        return ;
      }
      this.projectDataInfo.queryDuration = menuItem.key;
      common.saveQueryDuration(this.projectDataInfo.queryDuration);
      this.onSearch();
    },
    editProjectSuccess(responseEntity, project) {
      project.questUrl = responseEntity.questUrl;
      project.projectName = responseEntity.projectName;
      project.companyName = responseEntity.companyName;
      project.projectConfig = responseEntity.projectConfig;
      project.status = responseEntity.status;
      project.mingoCheckDateStart = responseEntity.mingoCheckDateStart;
      project.mingoCheckDateEnd = responseEntity.mingoCheckDateEnd;
      project.mingoExpireDateStart = responseEntity.mingoExpireDateStart;
      project.mingoExpireDateEnd = responseEntity.mingoExpireDateEnd;
      project.questTypeActions = responseEntity.questTypeActions;
      project.remark = responseEntity.remark;
      message.success("專案編號「" + responseEntity.projectNo + "」，修改成功。");
    },
    saveProjectI18nSuccess(project, questionnaireTitle, foreignerInputTip) {
      project.projectConfig.questionnaireTitle = questionnaireTitle;
      project.projectConfig.foreignerInputTip = foreignerInputTip;
      message.success("專案編號「" + project.projectNo + "」，多語系設定儲存成功。");
    },
    createProjectSuccess(responseEntity) {
      message.success("專案編號「" + responseEntity.projectNo + "」，新增成功。");
      this.onSearch();
    },
    onSearchChange() {
      if (common.isEmpty(this.projectDataInfo.searchText)) {
        this.onSearch();
      }
    },
    onSearch() {
      this.projectDataInfo.isLoading = true;
      common.postRequest(config.mgmtProjectEntities, {
        queryDuration: this.projectDataInfo.queryDuration,
        searchText: this.projectDataInfo.searchText,
        statusFilter: this.projectDataInfo.statusFilter,
        questTypeFilter: this.projectDataInfo.questTypeFilter
      }, (response) => {
        this.projectDataInfo.isLoading = false;
        this.projectDataInfo.data = response.data;
        this.projectDataInfo.expandedRows = [];
        this.projectDataInfo.pagination.current = 1;
      }, () => {
        this.projectDataInfo.isLoading = false;
        this.projectDataInfo.data = [];
        this.projectDataInfo.expandedRows = [];
        this.projectDataInfo.pagination.current = 1;
      });
    },
    reloadProjectData(page, filters) {
      this.projectDataInfo.pagination.current = page.current;
      this.projectDataInfo.pagination.pageSize = page.pageSize;
      filters.projectStatus = filters.projectStatus == null ? [] : filters.projectStatus;
      filters.questTypeActions = filters.questTypeActions == null ? [] : filters.questTypeActions;
      if (filters.projectStatus.toString() === this.projectDataInfo.statusFilter.toString() && 
          filters.questTypeActions.toString() === this.projectDataInfo.questTypeFilter.toString()) {
        return ;
      }
      this.projectDataInfo.statusFilter = filters.projectStatus;
      this.projectDataInfo.questTypeFilter = filters.questTypeActions;
      this.onSearch();
    },
    expandOnClick(expanded, record) {
      this.expandOnClickProject(record);
    },
    expandOnClickProject(record) {
      const keyIndex = this.projectDataInfo.expandedRows.indexOf(record.key);
      if (keyIndex >= 0) {
          this.projectDataInfo.expandedRows.splice(keyIndex, 1);
        } else {
          this.projectDataInfo.expandedRows.push(record.key);
        }
    },
    downloadImportUserSample() {
      common.downloadFile(config.downloadSampleImportUser, "人事資料範例.xlsx");
    },
    downloadUnImportUser(record) {
      const projectNo = record.projectNo;
      this.projectDataInfo.isLoading = true;
      common.downloadFile(config.projectUnImportDownloadXlsx + record.url, projectNo + "-未匯入人員清單.xlsx", 
        () => {
          this.projectDataInfo.isLoading = false;
        }, () => {
          this.projectDataInfo.isLoading = false;
          message.error("下載未匯入人員失敗，請重新登入。");
        }, () => {
          this.projectDataInfo.isLoading = false;
          common.openInfoModal("下載未匯入人員 - 訊息", "找不到未匯入人員資訊！\n" + 
            "專案編號「" + projectNo + "」");
        });
    },
    downloadImportedUser(record) {
      const projectNo = record.projectNo;
      this.projectDataInfo.isLoading = true;
      common.downloadFile(config.projectImportedDownloadXlsx + record.url, projectNo + "-已匯入名單.xlsx", 
        () => {
          this.projectDataInfo.isLoading = false;
        }, () => {
          this.projectDataInfo.isLoading = false;
          message.error("下載已匯入名單失敗，請重新登入。");
        }, () => {
          this.projectDataInfo.isLoading = false;
          common.openInfoModal("下載已匯入名單 - 訊息", "找不到已匯入人員資訊！\n" + 
            "專案編號「" + projectNo + "」");
        });
    },
    beforeUploadProjectUser(uploadFile) {
      if (uploadFile.name.endsWith('.xls') || uploadFile.name.endsWith('.xlsx')) {
        this.projectDataInfo.uploadFile = uploadFile;
      } else {
        this.projectDataInfo.uploadFile = null;
        const modalWidth = common.getTextLength(uploadFile.name) * 8 + 280;
        common.openErrorModal(modalWidth, "上傳失敗", "你所上傳的檔案格式有誤 [" + 
          uploadFile.name + "]\n只接受 XLS 或 XLSX 格式的檔案！");
      }
      return false;
    },
    uploadProjectUser(record) {
      if (this.projectDataInfo.uploadFile == null) {
        return ;
      }
      const modalWidth = common.getTextLength(this.projectDataInfo.uploadFile.name) * 8 + 300;
      const projectData = this.projectDataInfo;
      common.openConfirmModal(modalWidth, '匯入確認', "是否確認從檔案 [" + projectData.uploadFile.name + "] 匯入至以下專案\n" + 
        "專案編號「" + record.projectNo + "」\n" + 
        "專案名稱「" + record.projectName + "」\n" + 
        "客戶名稱「" + record.companyName + "」", () => {
          return new Promise(function(resolve, reject) {
            const formData = new FormData();
            formData.append('projectUrl', record.url);
            formData.append('file', projectData.uploadFile);

            projectData.isLoading = true;
            common.postFormDataRequest(config.importProjectUser, formData, () => {
              projectData.isLoading = false;
              message.success("專案編號「" + record.projectNo + "」，匯入成功。");
              resolve();
            }, (error) => {
              projectData.isLoading = false;
              common.openErrorModal(800, "匯入失敗", "專案編號「" + record.projectNo + "」人員匯入時發生錯誤！\n" + error.response.data);
              reject();
            });
          });
        });
    },
    deleteProject(record) {
      const reloadDataTable = this.onSearch;
      common.openConfirmModal(500, '刪除確認', "是否確認刪除以下專案：\n" + 
        "專案編號「" + record.projectNo + "」\n" + 
        "專案名稱「" + record.projectName + "」\n" +
        "客戶名稱「" + record.companyName + "」", () => {
          return new Promise(function(resolve, reject) {
            common.postRequest(config.mgmtDeleteProjectEntity, {url: record.url}, 
              () => {
                  message.success("專案編號「" + record.projectNo + "」，刪除成功。");
                  resolve();
                  reloadDataTable();
              }, () => {
                reject();
              });
            });
      });
    },
    copyQuestUrl(questUrl) {
      navigator.clipboard.writeText(questUrl)
            .then(() => message.success('問卷網址複製成功 !'))
            .catch(() => message.error('問卷網址複製失敗! 請洽詢廠商。'));
    },
    downloadQuestQrCode(record) {
      const projectName = record.projectName;
      const companyName = record.companyName;
      common.downloadFile(config.mgmtQuestQrCode + record.url, projectName + "-" + companyName + ".png", 
        () => {},
        () => {
          message.error("下載 QRCode 失敗，請重新登入。");
        });
    },
    exportXlsProjectQuest(questTypeAction, index) {
      this.exportProjectQuestReport(questTypeAction, "ExportXls", "xls", index);
    }, 
    exportXlsxProjectQuest(questTypeAction, index) {
      this.exportProjectQuestReport(questTypeAction, "ExportXlsx", "xlsx", index);
    }, 
    exportPdfProjectQuest(questTypeAction, index, lang) {
      this.exportProjectQuestReport(questTypeAction, "ExportPdf", "pdf", index, lang);
    },
    exportProjectQuestReport(questTypeAction, questAction, fileExtension, index, lang) {
      this.projectDataInfo.isLoading = true;
      let project = this.getProjectByIndex(index);
      let filePrefixName = this.projectDataInfo.exportByProjectName ? project.projectName : project.companyName;
      common.downloadFileByPosting(config.mgmtProjectQuestExport, 
        this.getDownloadFileName(filePrefixName, questTypeAction.questType, fileExtension, lang), 
        {
          url: questTypeAction.url,
          includeUnImport: project.includeUnImport,
          categorizeByUnitName: this.projectDataInfo.categorizeByUnitName,
          questAction: questAction,
          questType: questTypeAction.questType,
          languageOption: lang
        }, () => {
          this.projectDataInfo.isLoading = false;
        }, () => {
          this.projectDataInfo.isLoading = false;
          common.openErrorModal(600, "匯出失敗", 
            "無法匯出「" + fileExtension.toUpperCase() + "」報表，請重新登入。\n" + 
            "專案編號「" + questTypeAction.projectNo + "」");
        }, () => {
          this.projectDataInfo.isLoading = false;
          common.openInfoModal("匯出報表 - 訊息", "找不到可匯出的「" +
            this.questTypeNames.get(questTypeAction.questType) + "」" + fileExtension.toUpperCase() + " 報表資料！\n" +
            "專案編號「" + questTypeAction.projectNo + "」");
        });
    },
    getProjectByIndex(index) {
      if (this.projectDataInfo.pagination.pageSize === undefined) {
        return this.projectDataInfo.data[index];
      }
      index += (this.projectDataInfo.pagination.current - 1) * this.projectDataInfo.pagination.pageSize;
      return this.projectDataInfo.data[index];
    },
    getDownloadFileName(filePrefixName, questType, fileExtension, lang) {
      if (lang == undefined || lang == null) {
        return filePrefixName + "-" + this.questTypeNames.get(questType) + "." + fileExtension;
      }
      const langOptionName = this.langOptionNames.get(lang);
      if (langOptionName == null || langOptionName.length === 0) {
        return filePrefixName + "-" + this.questTypeNames.get(questType) + "." + fileExtension;
      }
      return filePrefixName + "-" + this.questTypeNames.get(questType) + "-" + langOptionName + "." + fileExtension;
    }
  },
};
</script>
<style scoped>
button,
span {
  display: inline-flex;
  align-items: center;
}
.hyperlink {
  color: blue;
  cursor: pointer;
  text-decoration: underline;
}
</style>
